import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { Alert } from '../../models/alert.interface';
import { AlertType } from '../../enums/alertType.enum';

/**
 * Base component providing shared alert and loading spinner helpers.
 */
@Component({
  selector: 'app-base',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './base.component.html',
  styleUrls: ['./base.component.css'],
})
export class BaseComponent implements OnInit, OnDestroy {
  destroy$: Subject<null> = new Subject();

  /**
   * Alerts to be shown in the <app-alerts> component
   */
  alerts: Alert[] = [];
  /**
   * Flag, that states, if the loading spinner should be visible or not.
   */
  showLoadingSpinner = false;
  /**
   * A simple message to be shown below the loading spinner
   */
  loadingSpinnerMessage?: string;
  /**
   * A map of key/message pairs for multiple message to be shown below the loading spinner
   */
  loadingSpinnerMessagesByKey = new Map<string, string>();

  /**
   * Subject used to scroll to alerts. Should be added to <app-alerts>
   */
  scrollToAlertsSubject = new Subject<void>();

  constructor() {}

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  // *********************************************
  // Error handling
  // *********************************************

  /**
   * Deletes all alerts.
   */
  clearAlerts(): void {
    this.alerts.length = 0;
  }

  /**
   * Deletes all errors, while leaving all other alerts.
   */
  clearErrors(): void {
    const newAlerts: Alert[] = [];
    this.alerts.forEach((alert) => {
      if (alert.type === AlertType.Error) return;
      newAlerts.push(alert);
    });
    this.alerts = newAlerts;
  }

  /**
   * Adds an alert of type Error.
   * @param message alert message
   */
  addError(message: string): void {
    this.alerts.push({ message, type: AlertType.Error, date: new Date() });
    this.scrollToAlertsSubject.next();
  }

  /**
   * Adds an alert of type Info.
   * @param message alert message
   */
  addInfo(message: string): void {
    this.alerts.push({ message, type: AlertType.Info, date: new Date() });
    this.scrollToAlertsSubject.next();
  }

  /**
   * Adds an alert of type Warning.
   * @param message alert message
   */
  addWarning(message: string): void {
    this.alerts.push({ message, type: AlertType.Warning, date: new Date() });
    this.scrollToAlertsSubject.next();
  }

  /**
   * Adds an alert of type Success.
   * @param message alert message
   */
  addSuccess(message: string): void {
    this.alerts.push({ message, type: AlertType.Success, date: new Date() });
    this.scrollToAlertsSubject.next();
  }

  getErrors(): Alert[] {
    const errors: Alert[] = [];
    this.alerts.forEach((alert) => {
      if (alert.type === AlertType.Error) errors.push(alert);
    });
    return errors;
  }

  /**
   * Shows the loading spinner and sets the given message (if there is any).
   * @param message message to be shown or undefined
   */
  enableLoadingSpinner(message?: string): void {
    this.showLoadingSpinner = true;
    this.loadingSpinnerMessage = message;
  }

  /**
   * Hides the loading spinner and deletes its message.
   */
  disableLoadingSpinner(): void {
    this.showLoadingSpinner = false;
    this.loadingSpinnerMessage = undefined;
  }

  /**
   * Adds a message with the given key to the loading spinner messages map and enables the loading spinner.
   * @param key key of the message, e.g. 'loadingListing'
   * @param message message to be shown below the loading spinner
   */
  addLoadingSpinnerMessage(key: string, message: string): void {
    this.showLoadingSpinner = true;
    this.loadingSpinnerMessagesByKey.set(key, message);
  }

  /**
   * Removes the message with the given key from the loading spinner. If it was the last one, hides the spinner.
   * @param key key of the message to be removed, e.g. 'loadingListing'
   */
  removeLoadingSpinnerMessage(key: string): void {
    this.loadingSpinnerMessagesByKey.delete(key);
    if (this.loadingSpinnerMessagesByKey.size === 0) this.showLoadingSpinner = false;
  }
}
